import {
    sixesCellDisplayFromDemoLabel,
    sixesCellDisplayFromSentence,
    type SixesCellDisplay,
} from "./sixesCellDisplay";
import { normalizeSixesBucket, type SixesBucket } from "./sixesMarketBucket";

export const SIXES_MARKET_CELL_COUNT = 32;

/** Column through the centre multiplier (rows 0, 1, 4, 5 @ col 3). */
export const SIXES_COL_THROUGH_MULTIPLIER = [3, 9, 23, 29] as const;
/** Row through the centre multiplier (row 3 side cells). */
export const SIXES_ROW_THROUGH_MULTIPLIER = [16, 17, 18, 19] as const;

/** Row/col for each market cell in the 6×6 grid (centre 2×2 is the multiplier). */
export function marketCellGridPosition(index: number): { row: number; col: number } {
    if (index < 6) return { row: 0, col: index };
    if (index < 12) return { row: 1, col: index - 6 };
    if (index < 14) return { row: 2, col: index - 12 };
    if (index < 16) return { row: 2, col: index - 14 + 4 };
    if (index < 18) return { row: 3, col: index - 16 };
    if (index < 20) return { row: 3, col: index - 18 + 4 };
    if (index < 26) return { row: 4, col: index - 20 };
    return { row: 5, col: index - 26 };
}

export function marketCellIndexFromGrid(row: number, col: number): number {
    for (let i = 0; i < SIXES_MARKET_CELL_COUNT; i += 1) {
        const pos = marketCellGridPosition(i);
        if (pos.row === row && pos.col === col) return i;
    }
    return -1;
}

export type SixesGridCellSlot = SixesCellDisplay;

export function emptySixesGridCellSlot(): SixesGridCellSlot {
    return { shortLabel: "", overUnderLine: null, fullSentence: "", bannerEmphasis: "", statLabel: "RESULT?", bucket: null };
}

/** @deprecated Use buildMarketCellsFromApiCells */
export function buildMarketCellLabelsFromApiCells(
    cells: ReadonlyArray<{ row: number; col: number; sentence: string }>,
): string[] {
    return buildMarketCellsFromApiCells(cells).map((c) => c.shortLabel || "");
}

export function buildMarketCellsFromApiCells(
    cells: ReadonlyArray<{ row: number; col: number; sentence: string; bucket?: string | null }>,
    opts: { homeTeam?: string; awayTeam?: string } = {},
): SixesGridCellSlot[] {
    const slots = Array.from({ length: SIXES_MARKET_CELL_COUNT }, () => emptySixesGridCellSlot());
    for (const cell of cells) {
        const idx = marketCellIndexFromGrid(cell.row, cell.col);
        if (idx >= 0) {
            const slot = sixesCellDisplayFromSentence(cell.sentence, opts);
            const persisted = normalizeSixesBucket(cell.bucket ?? null);
            slots[idx] = persisted ? { ...slot, bucket: persisted } : slot;
        }
    }
    return slots;
}

export function buildDemoMarketCells(
    labels: readonly string[],
    homeTeam: string,
    awayTeam: string,
    buckets: readonly (SixesBucket | null)[] = [],
): SixesGridCellSlot[] {
    return labels.map((label, idx) => {
        const slot = sixesCellDisplayFromDemoLabel(label, homeTeam, awayTeam);
        const bucket = buckets[idx];
        return bucket !== undefined ? { ...slot, bucket } : slot;
    });
}
